import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Phone, PhoneIncoming, X, Search, Minimize2, Radio, FileText, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import PhoneLookupPopup from "@/components/PhoneLookupPopup";

// Format phone for display: (212) 555-1234
const formatPhone = (num) => {
  if (!num) return "";
  let digits = num.replace(/\D/g, "");
  if (digits.startsWith("1") && digits.length === 11) digits = digits.slice(1);
  if (digits.length === 10) {
    return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
  }
  return num;
};

// Format countdown from now until expiresAt
const getCountdown = (expiresAt) => {
  if (!expiresAt) return "";
  try {
    const exp = new Date(expiresAt);
    const now = new Date();
    const diff = Math.max(0, Math.floor((exp - now) / 1000));
    const mins = Math.floor(diff / 60);
    const secs = diff % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  } catch { return ""; }
};

// Normalize caller number to 10-digit
const normalizeNumber = (num) => {
  const digits = (num || "").replace(/\D/g, "");
  return digits.startsWith("1") && digits.length === 11 ? digits.slice(1) : digits;
};

export default function IncomingCallAlert() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isDispatcher, setIsDispatcher] = useState(false);
  const [calls, setCalls] = useState([]);
  const [dismissedIds, setDismissedIds] = useState(new Set());
  const [collapsed, setCollapsed] = useState(false);
  const [lookupPhone, setLookupPhone] = useState(null);
  const [mode, setMode] = useState("live_call"); // "live_call" or "incident"
  const [selectMode, setSelectMode] = useState(false); // multi-select via long-press
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [, setTick] = useState(0); // Force re-render for countdown
  const pollRef = useRef(null);
  const countdownRef = useRef(null);
  const hiddenRef = useRef(false);
  const longPressRef = useRef(null);

  // Check if user is a dispatcher (admins always, or any user whose linked unit has is_dispatcher)
  useEffect(() => {
    if (!user) return;
    if (user.role === "admin") {
      setIsDispatcher(true);
      return;
    }
    if (!user.linked_unit) {
      setIsDispatcher(false);
      return;
    }
    axios.get(`${API}/units/dispatchers`, { withCredentials: true })
      .then(res => {
        const dispatcherNames = res.data.map(d => d.name);
        setIsDispatcher(dispatcherNames.includes(user.linked_unit));
      })
      .catch(() => setIsDispatcher(false));
  }, [user]);

  // Fetch alerts
  const fetchAlerts = useCallback(async () => {
    if (hiddenRef.current) return;
    try {
      const res = await axios.get(`${API}/incoming-calls/dispatcher-alerts`, { withCredentials: true });
      setCalls(res.data || []);
    } catch (err) {
      // Silently fail — non-critical
    }
  }, []);

  // Poll every 5 seconds
  useEffect(() => {
    if (!isDispatcher) return;
    fetchAlerts();
    pollRef.current = setInterval(fetchAlerts, 5000);

    const onVisibility = () => {
      hiddenRef.current = document.hidden;
      if (!document.hidden) fetchAlerts();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      clearInterval(pollRef.current);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [isDispatcher, fetchAlerts]);

  // 1-second countdown timer
  useEffect(() => {
    if (!isDispatcher) return;
    countdownRef.current = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(countdownRef.current);
  }, [isDispatcher]);

  // Filter out dismissed and expired calls
  const activeCalls = calls.filter(c => {
    if (dismissedIds.has(c.call_id)) return false;
    if (c.expires_at) {
      try {
        if (new Date(c.expires_at) <= new Date()) return false;
      } catch { /* keep */ }
    }
    return true;
  });

  // Exit select mode if no active calls left
  useEffect(() => {
    if (activeCalls.length === 0 && selectMode) {
      setSelectMode(false);
      setSelectedIds(new Set());
    }
  }, [activeCalls.length, selectMode]);

  // Don't render if not dispatcher or no calls
  if (!isDispatcher || activeCalls.length === 0) return null;

  // Navigate with numbers pre-filled
  const goWithNumbers = (numbers) => {
    sessionStorage.setItem("hotline_callback_numbers", JSON.stringify(numbers));
    // Quick Dispatch lands on the same page as Live Call, just in the short
    // form. A separate key so the stored numbers keep their shape — the
    // Incidents page reads them too.
    if (mode === "quick_call") sessionStorage.setItem("hotline_callback_quick", "1");
    else sessionStorage.removeItem("hotline_callback_quick");
    const path = mode === "incident" ? "/incidents" : "/live-calls";
    const primaryNumber = numbers.length > 0 ? numbers[0].number : "";
    const params = mode === "incident"
      ? `from_hotline_alert=1&new=true${primaryNumber ? `&callback=${encodeURIComponent(primaryNumber)}` : ""}`
      : "from_hotline_alert=1";
    navigate(`${path}?${params}`);
    // Fire custom event for when already on the target page (navigate won't remount)
    // Include target so only the right page picks it up
    window.dispatchEvent(new CustomEvent("hotline-alert-pickup", { detail: { target: path } }));
  };

  // Build callback number object from a call
  const toCallbackNumber = (call) => ({
    number: normalizeNumber(call.caller_number),
    caller_type: "",
    has_visual: false
  });

  // Single tap on a row
  const handleRowTap = (call) => {
    if (selectMode) {
      // In select mode, toggle checkbox
      toggleSelect(call.call_id);
    } else {
      // Single tap: immediately act on this number
      goWithNumbers([toCallbackNumber(call)]);
    }
  };

  // Long-press to enter select mode
  const handlePointerDown = (call) => {
    longPressRef.current = setTimeout(() => {
      setSelectMode(true);
      setSelectedIds(new Set([call.call_id]));
      longPressRef.current = null;
    }, 500);
  };

  const handlePointerUp = () => {
    if (longPressRef.current) {
      clearTimeout(longPressRef.current);
      longPressRef.current = null;
    }
  };

  const toggleSelect = (callId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(callId)) next.delete(callId);
      else next.add(callId);
      return next;
    });
  };

  // Multi-select: go with all selected
  const handleGoSelected = () => {
    const selected = activeCalls.filter(c => selectedIds.has(c.call_id));
    if (selected.length === 0) return;
    goWithNumbers(selected.map(toCallbackNumber));
    setSelectMode(false);
    setSelectedIds(new Set());
  };

  const cancelSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
  };

  const dismissAll = () => {
    setDismissedIds(prev => {
      const next = new Set(prev);
      activeCalls.forEach(c => next.add(c.call_id));
      return next;
    });
    setSelectMode(false);
    setSelectedIds(new Set());
  };

  const handleSearch = (e, callerNumber) => {
    e.stopPropagation(); // Don't trigger row tap
    setLookupPhone(normalizeNumber(callerNumber));
  };

  // Collapsed state: just pulsing icon + count
  if (collapsed) {
    return (
      <div
        className="fixed bottom-4 right-4 z-[100] cursor-pointer"
        onClick={() => setCollapsed(false)}
      >
        <div className="relative bg-red-600 text-white rounded-full w-12 h-12 flex items-center justify-center shadow-lg animate-pulse">
          <PhoneIncoming className="w-6 h-6" />
          <span className="absolute -top-1 -right-1 bg-white text-red-600 text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
            {activeCalls.length}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-[100] w-[320px] max-h-[400px] flex flex-col bg-white dark:bg-slate-900 border border-red-300 dark:border-red-700 rounded-lg shadow-2xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-red-600 text-white">
        <div className="flex items-center gap-2">
          <PhoneIncoming className="w-4 h-4 animate-pulse" />
          <span className="text-sm font-semibold">Incoming Calls ({activeCalls.length})</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setCollapsed(true)} className="p-1 hover:bg-red-700 rounded">
            <Minimize2 className="w-3.5 h-3.5" />
          </button>
          <button onClick={dismissAll} className="p-1 hover:bg-red-700 rounded" title="Dismiss all">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Call list */}
      <div className="flex-1 overflow-y-auto max-h-[260px]">
        {activeCalls.map(call => {
          const countdown = getCountdown(call.expires_at);
          const isSelected = selectedIds.has(call.call_id);
          return (
            <div
              key={call.call_id}
              className={`flex items-center gap-2 px-3 py-2 border-b border-slate-200 dark:border-slate-700 cursor-pointer select-none transition-colors
                ${isSelected ? "bg-red-50 dark:bg-red-950" : "hover:bg-slate-50 dark:hover:bg-slate-800"}`}
              onClick={() => handleRowTap(call)}
              onPointerDown={() => handlePointerDown(call)}
              onPointerUp={handlePointerUp}
              onPointerLeave={handlePointerUp}
            >
              {selectMode && (
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => toggleSelect(call.call_id)}
                  onClick={(e) => e.stopPropagation()}
                  className="shrink-0"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <Phone className="w-3.5 h-3.5 text-red-500 shrink-0" />
                  <span className="text-sm font-medium truncate">{formatPhone(call.caller_number)}</span>
                  {call.contact_name && (
                    <span className="text-xs text-muted-foreground truncate">{call.contact_name}</span>
                  )}
                  {(call.contact_info?.known || call.contact_status === "flagged") && (
                    <Badge variant="outline" className={`text-[10px] px-1 py-0 shrink-0 ${
                      call.contact_status === "flagged" ? "border-yellow-500 text-yellow-500" : "border-amber-500 text-amber-600"
                    }`}>
                      {call.contact_status === "flagged" ? "Flagged" : "Known"}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs text-muted-foreground">{call.received_at_ny}</span>
                  <span className="text-xs text-red-500 font-mono">{countdown} left</span>
                </div>
                {call.contact_info?.headline && (
                  <p className="text-[11px] text-muted-foreground truncate mt-0.5">
                    {call.contact_info.incident_id}: {call.contact_info.headline}
                  </p>
                )}
                {call.contact_info?.call_type && (
                  <p className="text-[11px] text-muted-foreground truncate mt-0.5">
                    Live call: {call.contact_info.call_type}
                  </p>
                )}
              </div>
              <button
                onClick={(e) => handleSearch(e, call.caller_number)}
                className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-700 rounded shrink-0"
                title="Lookup phone number"
              >
                <Search className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="px-3 py-2 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 space-y-1.5">
        {/* Mode toggle */}
        <div className="flex items-center rounded-md overflow-hidden border border-slate-300 dark:border-slate-600">
          <button
            className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium transition-colors ${
              mode === "live_call"
                ? "bg-blue-600 text-white"
                : "bg-transparent text-muted-foreground hover:bg-slate-100 dark:hover:bg-slate-700"
            }`}
            onClick={() => setMode("live_call")}
          >
            <Radio className="w-3 h-3" />
            Live Call
          </button>
          <button
            className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium transition-colors border-l border-slate-300 dark:border-slate-600 ${
              mode === "quick_call"
                ? "bg-blue-600 text-white"
                : "bg-transparent text-muted-foreground hover:bg-slate-100 dark:hover:bg-slate-700"
            }`}
            onClick={() => setMode("quick_call")}
            title="Quick Dispatch — the short live-call form, callback already entered"
          >
            <Zap className="w-3 h-3" />
            Quick
          </button>
          <button
            className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium transition-colors border-l border-slate-300 dark:border-slate-600 ${
              mode === "incident"
                ? "bg-blue-600 text-white"
                : "bg-transparent text-muted-foreground hover:bg-slate-100 dark:hover:bg-slate-700"
            }`}
            onClick={() => setMode("incident")}
          >
            <FileText className="w-3 h-3" />
            Incident
          </button>
        </div>

        {/* Select mode actions */}
        {selectMode ? (
          <div className="flex items-center gap-1.5">
            <Button size="sm" variant="default" className="flex-1 h-7 text-xs" onClick={handleGoSelected} disabled={selectedIds.size === 0}>
              Create with {selectedIds.size} selected
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs px-2" onClick={cancelSelectMode}>
              Cancel
            </Button>
          </div>
        ) : (
          <p className="text-[10px] text-muted-foreground text-center">
            Tap to create &bull; Long-press for multi-select
          </p>
        )}
      </div>

      {/* Phone Lookup Popup */}
      <PhoneLookupPopup
        phone={lookupPhone}
        open={!!lookupPhone}
        onOpenChange={(open) => { if (!open) setLookupPhone(null); }}
      />
    </div>
  );
}
